import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { useUser } from '../contexts/UserContext';
import { useMatch } from '../contexts/MatchContext';
import { useAdmin } from '../contexts/AdminContext';
import './MobileSidebar.css';

import customIcon from '../assets/kategorien-icon.png';
import closeIcon from '../assets/close-icon.png';
import dailyIcon from '../assets/daily-icon.png';
import flagIcon from '../assets/flag-icon.png';
import countryIcon from '../assets/country-icon.png';
import profileIcon from '../assets/profile-icon.png';
import rankedIcon from '../assets/ranked-icon.png';
import articlesIcon from '../assets/articles-icon.png';
import leaderboardIcon from '../assets/leaderboard-icon.png';

const MobileSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { user, userData } = useUser();
    const { resetMatchState } = useMatch();
    const { isAdmin } = useAdmin();
    const sidebarRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);

    // Check if the current user is anonymous (using Firebase Auth's built-in flag)
    const isAnonymous = user?.isAnonymous;

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 300); // Match this with animation duration
    }, [onClose]);

    const handleLogout = async () => {
        try {
            // First reset match state if available
            if (resetMatchState) {
                resetMatchState();
            }

            // Then sign out
            await auth.signOut();

            // Navigate to the homepage and close sidebar
            navigate('/');
            handleClose();
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    // Function to handle navigation and close sidebar
    const handleNavigation = (path) => {
        navigate(path);
        handleClose();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClose]);

    return (
        <>
            <div className={`sidebar-overlay ${isClosing ? 'overlay-closing' : ''}`} onClick={handleClose}></div>
            <div ref={sidebarRef} className={`mobile-sidebar ${isClosing ? 'sidebar-closing' : ''}`}>
                <img loading="lazy" src={closeIcon} alt="Close" className="close-icon" onClick={handleClose} />

                <div className="sidebar-link" onClick={() => handleNavigation("/daily")}>
                    <img src={dailyIcon} alt="Endless Mode" className="sidebar-icon" />
                    <span className="sidebar-text">Daily</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/battle")}>
                    <img src={rankedIcon} alt="Battle" className="sidebar-icon" />
                    <span className="sidebar-text">Battle</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/custom")}>
                    <img src={customIcon} alt="Custom" className="sidebar-icon" />
                    <span className="sidebar-text">Custom</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/flag-quiz")}>
                    <img src={flagIcon} alt="Flag Quiz" className="sidebar-icon" />
                    <span className="sidebar-text">Flags</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/country-quiz")}>
                    <img src={countryIcon} alt="Country Quiz" className="sidebar-icon" />
                    <span className="sidebar-text">Countries</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/leaderboard")}>
                    <img src={leaderboardIcon} alt="Leaderboard" className="sidebar-icon" />
                    <span className="sidebar-text">Leaderboard</span>
                </div>

                <div className="sidebar-link" onClick={() => handleNavigation("/articles")}>
                    <img src={articlesIcon} alt="Articles" className="sidebar-icon" />
                    <span className="sidebar-text">Articles</span>
                </div>

                {user && !isAnonymous && (
                    <div className="sidebar-link" onClick={() => handleNavigation(`/profile/${userData?.username}`)}>
                        <img src={profileIcon} alt="Profile" className="sidebar-icon" />
                        <span className="sidebar-text">Profile</span>
                    </div>
                )}

                {/* Admin link - only shown for admin users */}
                {isAdmin && (
                    <div className="sidebar-link admin-link" onClick={() => handleNavigation("/admin")}>
                        <span className="sidebar-text">Admin Panel</span>
                    </div>
                )}

                {user && !isAnonymous ? (
                    <button className="logoutButton" onClick={handleLogout}>Log Out</button>
                ) : (
                    <>
                        <button className="registerButton" onClick={() => handleNavigation("/register")}>Sign Up</button>
                        <button className="loginButton" onClick={() => handleNavigation("/login")}>Log In</button>
                    </>
                )}
            </div>
        </>
    );
};

export default MobileSidebar;